import { IconForView, SchemaDriftBanner, StudioBridgeContext, StudioBridgeProvider, useNavigationGroupLabel, useRebaseContext, useRebaseRegistry, useRebaseRegistryDispatch, useRestoreScroll, useSlot, useStudioBreadcrumbs, useStudioBreadcrumbs as useStudioBreadcrumbs$1, useStudioCollectionRegistry, useStudioNavigationState, useStudioSidePanelController, useStudioUrlController, useTranslation } from "@rebasepro/app";
import React, { Suspense, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Card, CircularProgressCenter, Container, ExpandablePanel, Typography, cls, lazyChunk } from "@rebasepro/ui";
import { jsx, jsxs } from "react/jsx-runtime";
import { useNavigate } from "react-router";
//#region src/components/StudioHomePage.tsx
/** Group order for the home page; unknown groups are appended in view order. */
var GROUP_ORDER = [
	"Database",
	"Compute",
	"Storage",
	"API",
	"Access Control"
];
var UNGROUPED_LABEL = "Tools";
/**
* Build the home page sections from the Studio views that are actually
* registered, so a card exists if and only if its route does. A hand-written
* list drifts: it used to advertise Users and Roles pages that 404, while
* omitting the Backups view that does exist.
*/
function buildSections(views) {
	const byGroup = /* @__PURE__ */ new Map();
	for (const view of views) {
		if (view.hideFromNavigation) continue;
		const label = view.group ?? UNGROUPED_LABEL;
		const tools = byGroup.get(label) ?? [];
		tools.push({
			path: `/${view.slug}`,
			name: view.name,
			description: view.description ?? "",
			icon: view.icon
		});
		byGroup.set(label, tools);
	}
	return [...GROUP_ORDER.filter((g) => byGroup.has(g)), ...[...byGroup.keys()].filter((g) => !GROUP_ORDER.includes(g))].map((label) => ({
		label,
		tools: byGroup.get(label)
	}));
}
var COLLAPSED_STORAGE_KEY = "rebase-studio-home-collapsed";
function useStudioCollapsedGroups(groupNames) {
	const [collapsed, setCollapsed] = useState(() => {
		try {
			const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY);
			return stored ? new Set(JSON.parse(stored)) : /* @__PURE__ */ new Set();
		} catch {
			return /* @__PURE__ */ new Set();
		}
	});
	const isGroupCollapsed = (name) => collapsed.has(name);
	const toggleGroupCollapsed = (name) => {
		setCollapsed((prev) => {
			const next = new Set(prev);
			if (next.has(name)) next.delete(name);
			else next.add(name);
			try {
				localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify([...next]));
			} catch {}
			return next;
		});
	};
	return {
		isGroupCollapsed,
		toggleGroupCollapsed
	};
}
function StudioHomePage({ additionalActions, additionalChildrenStart, additionalChildrenEnd, sections, hiddenGroups }) {
	const context = useRebaseContext();
	const breadcrumbs = useStudioBreadcrumbs$1();
	const navigate = useNavigate();
	const registry = useRebaseRegistry();
	useEffect(() => {
		breadcrumbs.set({ breadcrumbs: [] });
	}, [breadcrumbs.set]);
	const { containerRef } = useRestoreScroll();
	const pluginActions = useSlot("home.actions", { context });
	const schemaEnabled = Boolean(registry.studioConfig && registry.cmsConfig?.collectionEditor);
	const filteredSections = useMemo(() => {
		const views = [];
		if (schemaEnabled) views.push({
			slug: "schema",
			name: "Collections",
			group: "Database",
			icon: "LayoutList",
			description: "Define and manage your data model and collection schemas"
		});
		views.push(...registry.studioConfig?.devViews ?? []);
		return buildSections(views).filter((s) => !hiddenGroups?.includes(s.label));
	}, [
		registry.studioConfig?.devViews,
		schemaEnabled,
		hiddenGroups
	]);
	const { isGroupCollapsed, toggleGroupCollapsed } = useStudioCollapsedGroups(useMemo(() => filteredSections.map((s) => s.label), [filteredSections]));
	const groupLabel = useNavigationGroupLabel();
	return /* @__PURE__ */ jsx("div", {
		ref: containerRef,
		className: "py-2 overflow-auto h-full w-full bg-surface-sheet",
		children: /* @__PURE__ */ jsxs(Container, {
			maxWidth: "6xl",
			children: [
				/* @__PURE__ */ jsx("div", {
					className: "mb-4 flex flex-col gap-2",
					children: /* @__PURE__ */ jsx(SchemaDriftBanner, {})
				}),
				(additionalActions || pluginActions) && /* @__PURE__ */ jsxs("div", {
					className: "w-full sticky py-4 transition-all duration-400 ease-in-out top-0 z-10 flex flex-row gap-4 justify-end",
					children: [additionalActions, pluginActions]
				}),
				additionalChildrenStart,
				filteredSections.map((section) => {
					const sectionCollapsed = isGroupCollapsed(section.label);
					return /* @__PURE__ */ jsx("div", {
						className: "my-10",
						children: /* @__PURE__ */ jsx(ExpandablePanel, {
							invisible: true,
							expanded: !sectionCollapsed,
							onExpandedChange: (open) => {
								if (open !== !sectionCollapsed) toggleGroupCollapsed(section.label);
							},
							className: "mt-6",
							titleClassName: cls("min-h-0 p-0 border-none", "rounded flex items-center justify-between w-full", "hover:bg-transparent", "cursor-pointer select-none", sectionCollapsed && "bg-surface-field"),
							innerClassName: "mt-4 pt-0",
							title: /* @__PURE__ */ jsx(Typography, {
								variant: "caption",
								component: "h2",
								color: "secondary",
								className: cls("px-4 py-1 rounded", "font-medium text-[10px] uppercase tracking-[0.08em] text-surface-400 dark:text-surface-400"),
								children: groupLabel(section.label)
							}),
							children: /* @__PURE__ */ jsx("div", {
								className: "mt-4 pt-0",
								children: /* @__PURE__ */ jsx("div", {
									className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
									children: section.tools.map((tool) => /* @__PURE__ */ jsx(Card, {
										onClick: () => {
											navigate(tool.path);
											context.analyticsController?.onAnalyticsEvent?.("home_navigate_to_view", { path: tool.path });
										},
										className: cls("group h-full p-4 cursor-pointer transition-colors duration-150 ease-in-out", "hover:bg-surface-card-hover"),
										children: /* @__PURE__ */ jsxs("div", {
											className: "flex flex-col h-full",
											children: [
												/* @__PURE__ */ jsx("div", {
													className: "flex items-center w-full justify-between mb-1",
													children: /* @__PURE__ */ jsxs("div", {
														className: "flex items-center gap-3",
														children: [/* @__PURE__ */ jsx("div", {
															className: "flex items-center justify-center w-5 h-5 text-surface-400 dark:text-surface-500 transition-colors duration-150 group-hover:text-primary dark:group-hover:text-primary",
															children: /* @__PURE__ */ jsx(IconForView, {
																collectionOrView: {
																	slug: tool.path,
																	name: tool.name,
																	icon: tool.icon
																},
																size: "small"
															})
														}), /* @__PURE__ */ jsx(Typography, {
															variant: "subtitle1",
															component: "h2",
															children: tool.name
														})]
													})
												}),
												/* @__PURE__ */ jsx("div", {
													className: "pl-8",
													children: tool.description && /* @__PURE__ */ jsx(Typography, {
														variant: "caption",
														color: "secondary",
														component: "div",
														children: tool.description
													})
												}),
												/* @__PURE__ */ jsx("div", { className: "grow" })
											]
										})
									}, tool.path))
								})
							})
						})
					}, section.label);
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "mt-10 mb-6",
					children: [
						/* @__PURE__ */ jsx("div", {
							className: "flex items-center mb-1",
							children: /* @__PURE__ */ jsx(Typography, {
								variant: "caption",
								component: "h2",
								color: "secondary",
								className: cls("px-4 py-1 rounded", "font-medium text-[10px] uppercase tracking-[0.08em] text-surface-400 dark:text-surface-400"),
								children: "Quick Start"
							})
						}),
						/* @__PURE__ */ jsxs(Typography, {
							variant: "body2",
							color: "secondary",
							className: "mb-4 max-w-2xl",
							children: [
								"Generate a fully-typed SDK from your collections with",
								" ",
								/* @__PURE__ */ jsx("code", {
									className: "text-emerald-400 font-mono text-xs bg-emerald-400/10 px-1.5 py-0.5 rounded",
									children: "npx rebase generate-sdk"
								}),
								" ",
								"and start querying your data with full TypeScript autocompletion."
							]
						}),
						/* @__PURE__ */ jsxs("div", {
							className: "rounded-lg border border-hairline bg-surface-card overflow-hidden",
							children: [/* @__PURE__ */ jsxs("div", {
								className: "flex items-center justify-between px-4 py-2.5 border-b border-hairline bg-surface-sheet",
								children: [/* @__PURE__ */ jsxs("div", {
									className: "flex items-center gap-2.5",
									children: [/* @__PURE__ */ jsxs("div", {
										className: "flex gap-1.5",
										children: [
											/* @__PURE__ */ jsx("span", { className: "w-2.5 h-2.5 rounded-full bg-red-400/60" }),
											/* @__PURE__ */ jsx("span", { className: "w-2.5 h-2.5 rounded-full bg-amber-400/60" }),
											/* @__PURE__ */ jsx("span", { className: "w-2.5 h-2.5 rounded-full bg-emerald-400/60" })
										]
									}), /* @__PURE__ */ jsx("span", {
										className: "text-xs font-mono text-surface-400 dark:text-surface-500 ml-1",
										children: "app.ts"
									})]
								}), /* @__PURE__ */ jsx("span", {
									className: "text-xs font-mono text-surface-400 dark:text-surface-500",
									children: "TypeScript"
								})]
							}), /* @__PURE__ */ jsx("div", {
								className: "px-5 py-4 overflow-x-auto text-[13px] leading-6 font-mono",
								children: /* @__PURE__ */ jsx(SyntaxHighlightedSnippet, {})
							})]
						})
					]
				}),
				sections?.map((s) => /* @__PURE__ */ jsxs("div", {
					className: "my-10",
					children: [/* @__PURE__ */ jsx(Typography, {
						variant: "caption",
						component: "h2",
						color: "secondary",
						className: cls("px-4 py-1 rounded", "font-medium text-[10px] uppercase tracking-[0.08em] text-surface-400 dark:text-surface-400"),
						children: s.title
					}), /* @__PURE__ */ jsx("div", {
						className: "mt-4",
						children: s.children
					})]
				}, s.key)),
				additionalChildrenEnd
			]
		})
	});
}
function SyntaxHighlightedSnippet() {
	const kw = "text-violet-600 dark:text-violet-400";
	const str = "text-emerald-600 dark:text-emerald-400";
	const typ = "text-amber-600 dark:text-amber-300";
	const fn = "text-blue-600 dark:text-blue-400";
	const cm = "text-surface-500 dark:text-surface-400 italic";
	const op = "text-surface-500 dark:text-surface-400";
	const tx = "text-surface-950 dark:text-surface-200";
	return /* @__PURE__ */ jsxs("pre", {
		className: "m-0 whitespace-pre",
		children: [
			/* @__PURE__ */ jsx("span", {
				className: kw,
				children: "import"
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: " { "
			}),
			/* @__PURE__ */ jsx("span", {
				className: fn,
				children: "createRebaseClient"
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: " } "
			}),
			/* @__PURE__ */ jsx("span", {
				className: kw,
				children: "from"
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: " "
			}),
			/* @__PURE__ */ jsx("span", {
				className: str,
				children: "'@rebasepro/client'"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: ";"
			}),
			"\n",
			/* @__PURE__ */ jsx("span", {
				className: kw,
				children: "import"
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: " "
			}),
			/* @__PURE__ */ jsx("span", {
				className: kw,
				children: "type"
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: " { "
			}),
			/* @__PURE__ */ jsx("span", {
				className: typ,
				children: "Database"
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: " } "
			}),
			/* @__PURE__ */ jsx("span", {
				className: kw,
				children: "from"
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: " "
			}),
			/* @__PURE__ */ jsx("span", {
				className: str,
				children: "'./database.types'"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: ";"
			}),
			"\n\n",
			/* @__PURE__ */ jsx("span", {
				className: kw,
				children: "const"
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: " rebase "
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: "= "
			}),
			/* @__PURE__ */ jsx("span", {
				className: fn,
				children: "createRebaseClient"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: "<"
			}),
			/* @__PURE__ */ jsx("span", {
				className: typ,
				children: "Database"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: ">("
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: "{"
			}),
			"\n",
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: "    baseUrl"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: ": "
			}),
			/* @__PURE__ */ jsx("span", {
				className: str,
				children: "'http://localhost:3001'"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: ","
			}),
			"\n",
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: "}"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: ");"
			}),
			"\n\n",
			/* @__PURE__ */ jsx("span", {
				className: cm,
				children: "// Fully typed — autocompletion for tables and columns"
			}),
			"\n",
			/* @__PURE__ */ jsx("span", {
				className: kw,
				children: "const"
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: " { "
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: "data"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: ": "
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: "users"
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: " } "
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: "= "
			}),
			/* @__PURE__ */ jsx("span", {
				className: kw,
				children: "await"
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: " rebase"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: "."
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: "data"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: "."
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: "users"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: "."
			}),
			/* @__PURE__ */ jsx("span", {
				className: fn,
				children: "find"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: "();"
			}),
			"\n",
			/* @__PURE__ */ jsx("span", {
				className: kw,
				children: "const"
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: " { "
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: "data"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: ": "
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: "posts"
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: " } "
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: "= "
			}),
			/* @__PURE__ */ jsx("span", {
				className: kw,
				children: "await"
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: " rebase"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: "."
			}),
			/* @__PURE__ */ jsx("span", {
				className: tx,
				children: "data"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: "."
			}),
			/* @__PURE__ */ jsx("span", {
				className: fn,
				children: "collection"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: "("
			}),
			/* @__PURE__ */ jsx("span", {
				className: str,
				children: "'posts'"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: ")"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: "."
			}),
			/* @__PURE__ */ jsx("span", {
				className: fn,
				children: "find"
			}),
			/* @__PURE__ */ jsx("span", {
				className: op,
				children: "();"
			})
		]
	});
}
//#endregion
//#region src/components/RebaseStudio.tsx
var SQLEditor = lazyChunk(() => import("./SQLEditor-sdtoZv5F.js").then((m) => ({ default: m.SQLEditor })));
var JSEditor = lazyChunk(() => import("./JSEditor-qiuwmcvk.js").then((m) => ({ default: m.JSEditor })));
var RLSEditor = lazyChunk(() => import("./RLSEditor-B-qrgdHy.js").then((m) => ({ default: m.RLSEditor })));
var StorageView = lazyChunk(() => import("./StorageView-C52dSZNf.js").then((m) => ({ default: m.StorageView })));
var CronJobsView = lazyChunk(() => import("./CronJobsView-JwRjQCde.js").then((m) => ({ default: m.CronJobsView })));
var SchemaVisualizer = lazyChunk(() => import("./SchemaVisualizer-DtDzIUNy.js").then((m) => ({ default: m.SchemaVisualizer })));
var BranchesView = lazyChunk(() => import("./BranchesView-D6Sz1a5f.js").then((m) => ({ default: m.BranchesView })));
var BackupsView = lazyChunk(() => import("./BackupsView-DYTAD6aW.js").then((m) => ({ default: m.BackupsView })));
var ApiExplorer = lazyChunk(() => import("./ApiExplorer-D-jLWAaY.js").then((m) => ({ default: m.ApiExplorer })));
var LogsExplorer = lazyChunk(() => import("./LogsExplorer-Xq8K4oo6.js").then((m) => ({ default: m.LogsExplorer })));
var ApiKeysView = lazyChunk(() => import("./ApiKeysView-OfJLH2x7.js").then((m) => ({ default: m.ApiKeysView })));
/**
* Declarative component to configure the Studio in Rebase.
* Renders nothing — purely registers config into the RebaseRegistry.
*
* The "schema" tool (collection editor view) is a Studio tool, but it is not
* registered here: it ships from the panel package because it needs the project's
* collection source to write back to. When `<RebaseCMS collectionEditor={...}>`
* is used, the schema view is automatically injected into Studio — no manual
* wiring needed. Where it is mounted from is an implementation detail; it is a
* schema-editing tool and belongs beside SQL and RLS.
*/
var DEFAULT_HOME_PAGE = /* @__PURE__ */ jsx(StudioHomePage, {});
function RebaseStudio({ tools, homePage, devViews: extraViews }) {
	const dispatch = useRebaseRegistryDispatch();
	const { t, i18n } = useTranslation();
	const resolvedHomePage = homePage ?? DEFAULT_HOME_PAGE;
	const devViews = useMemo(() => {
		const views = [];
		const activeTools = tools ?? [
			"sql",
			"js",
			"rls",
			"storage",
			"cron",
			"schema-visualizer",
			"branches",
			"backups",
			"api",
			"logs",
			"api-keys"
		];
		const suspense = (el) => /* @__PURE__ */ jsx(Suspense, {
			fallback: /* @__PURE__ */ jsx(CircularProgressCenter, {}),
			children: el
		});
		if (activeTools.includes("sql")) views.push({
			slug: "sql",
			name: t("studio_tool_sql"),
			group: "Database",
			icon: "terminal",
			description: t("studio_tool_sql_description"),
			view: suspense(/* @__PURE__ */ jsx(SQLEditor, {}))
		});
		if (activeTools.includes("js")) views.push({
			slug: "js",
			name: t("studio_tool_js"),
			group: "Compute",
			icon: "code",
			description: t("studio_tool_js_description"),
			view: suspense(/* @__PURE__ */ jsx(JSEditor, {}))
		});
		if (activeTools.includes("rls")) views.push({
			slug: "rls",
			name: t("studio_tool_rls"),
			group: "Database",
			icon: "ShieldCheck",
			description: t("studio_tool_rls_description"),
			view: suspense(/* @__PURE__ */ jsx(RLSEditor, {}))
		});
		if (activeTools.includes("storage")) views.push({
			slug: "storage",
			name: t("studio_tool_storage"),
			group: "Storage",
			icon: "HardDrive",
			description: t("studio_tool_storage_description"),
			view: suspense(/* @__PURE__ */ jsx(StorageView, {}))
		});
		if (activeTools.includes("cron")) views.push({
			slug: "cron",
			name: t("studio_tool_cron"),
			group: "Compute",
			icon: "Clock",
			description: t("studio_tool_cron_description"),
			view: suspense(/* @__PURE__ */ jsx(CronJobsView, {}))
		});
		if (activeTools.includes("schema-visualizer")) views.push({
			slug: "schema-visualizer",
			name: t("studio_tool_schema_visualizer"),
			group: "Database",
			icon: "Network",
			description: t("studio_tool_schema_visualizer_description"),
			view: suspense(/* @__PURE__ */ jsx(SchemaVisualizer, {}))
		});
		if (activeTools.includes("branches")) views.push({
			slug: "branches",
			name: t("studio_tool_branches"),
			group: "Database",
			icon: "GitBranch",
			description: t("studio_tool_branches_description"),
			view: suspense(/* @__PURE__ */ jsx(BranchesView, {}))
		});
		if (activeTools.includes("backups")) views.push({
			slug: "backups",
			name: t("studio_tool_backups"),
			group: "Database",
			icon: "Database",
			description: t("studio_tool_backups_description"),
			view: suspense(/* @__PURE__ */ jsx(BackupsView, {}))
		});
		if (activeTools.includes("api")) views.push({
			slug: "api",
			name: t("studio_tool_api"),
			group: "API",
			icon: "BookOpen",
			description: t("studio_tool_api_description"),
			view: suspense(/* @__PURE__ */ jsx(ApiExplorer, {}))
		});
		if (activeTools.includes("logs")) views.push({
			slug: "logs",
			name: t("studio_tool_logs"),
			group: "Database",
			icon: "Activity",
			description: t("studio_tool_logs_description"),
			view: suspense(/* @__PURE__ */ jsx(LogsExplorer, {}))
		});
		if (activeTools.includes("api-keys")) views.push({
			slug: "api-keys",
			name: t("studio_tool_api_keys"),
			group: "Access Control",
			icon: "KeyRound",
			description: t("studio_tool_api_keys_description"),
			view: suspense(/* @__PURE__ */ jsx(ApiKeysView, {}))
		});
		return views;
	}, [
		tools ? tools.join(",") : "",
		t,
		i18n.language
	]);
	/**
	* Your own tools, keyed the same way `tools` is and for the same reason:
	* callers write `devViews={[…]}` inline, so the array is new on every
	* render. Re-registering would tear down and remount whichever Studio view
	* was on screen — a half-typed SQL query, an open policy editor.
	*
	* The key is the identity of each view, not its element: a view's `view` is
	* resolved by the router at render time, so a fresh element for the same
	* slug is not a configuration change.
	*/
	const extraViewsKey = (extraViews ?? []).map((v) => `${v.slug}|${v.name}|${v.group ?? ""}|${v.hideFromNavigation ?? false}`).join(",");
	const extraViewsRef = React.useRef(extraViews);
	extraViewsRef.current = extraViews;
	const allDevViews = useMemo(() => [...devViews, ...extraViewsRef.current ?? []], [devViews, extraViewsKey]);
	const homePageRef = React.useRef(resolvedHomePage);
	homePageRef.current = resolvedHomePage;
	const toolsRef = React.useRef(tools);
	toolsRef.current = tools;
	useLayoutEffect(() => {
		dispatch.registerStudio({
			tools: toolsRef.current,
			homePage: homePageRef.current,
			devViews: allDevViews
		});
		return () => dispatch.unregisterStudio();
	}, [dispatch, allDevViews]);
	return null;
}
//#endregion
export { RebaseStudio, StudioBridgeContext, StudioBridgeProvider, StudioHomePage, useStudioBreadcrumbs, useStudioCollectionRegistry, useStudioNavigationState, useStudioSidePanelController, useStudioUrlController };

//# sourceMappingURL=index.es.js.map